
import React from 'react'
import {ArrowLeft, Building2, Users, Award, Target, Globe, Phone, Mail, MapPin, ExternalLink} from 'lucide-react'
import { Link } from 'react-router-dom'

const Instituciones = () => {
  const instituciones = [
    {
      nombre: "Gobierno del Estado de Chiapas",
      dependencia: "Secretaría de Medio Ambiente e Historia Natural",
      descripcion: "Entidad coordinadora del proyecto, responsable de la política ambiental estatal y la gestión de recursos naturales.",
      logo: "🏛️",
      funciones: [
        "Coordinación general del proyecto",
        "Definición de políticas ambientales",
        "Gestión de recursos hídricos",
        "Supervisión técnica"
      ],
      contacto: {
        direccion: "Tuxtla Gutiérrez, Chiapas",
        telefono: "+52 961 xxx xxxx",
        email: "contacto@semahn.chiapas.gob.mx",
        web: "www.semahn.chiapas.gob.mx"
      },
      color: "blue"
    },
    {
      nombre: "Universidad de Ciencias y Artes de Chiapas",
      dependencia: "UNICACH",
      descripcion: "Institución de educación superior que aporta investigación científica y desarrollo tecnológico al proyecto.",
      logo: "🎓",
      funciones: [
        "Investigación científica",
        "Desarrollo metodológico",
        "Análisis de datos",
        "Capacitación técnica"
      ],
      contacto: {
        direccion: "Tuxtla Gutiérrez, Chiapas",
        telefono: "+52 961 xxx xxxx",
        email: "investigacion@unicach.mx",
        web: "www.unicach.mx"
      },
      color: "green"
    },
    {
      nombre: "Centro de Investigación en Gestión de Riesgos",
      dependencia: "y Cambio Climático",
      descripcion: "Centro especializado en estudios ambientales, gestión de riesgos y análisis de cambio climático.",
      logo: "🔬",
      funciones: [
        "Análisis de vulnerabilidad",
        "Modelado climático",
        "Evaluación de riesgos",
        "Desarrollo de indicadores"
      ],
      contacto: {
        direccion: "San Cristóbal de Las Casas, Chiapas",
        telefono: "+52 967 xxx xxxx",
        email: "info@cigr.mx",
        web: "www.cigr.mx"
      },
      color: "purple"
    }
  ]

  const colaboradores = [
    { nombre: "CONAGUA", descripcion: "Comisión Nacional del Agua", tipo: "Federal" },
    { nombre: "INEGI", descripcion: "Instituto Nacional de Estadística y Geografía", tipo: "Federal" },
    { nombre: "CONABIO", descripcion: "Comisión Nacional para el Conocimiento y Uso de la Biodiversidad", tipo: "Federal" },
    { nombre: "SMN", descripcion: "Servicio Meteorológico Nacional", tipo: "Federal" },
    { nombre: "CONAPO", descripcion: "Consejo Nacional de Población", tipo: "Federal" },
    { nombre: "SEMARNAT", descripcion: "Secretaría de Medio Ambiente y Recursos Naturales", tipo: "Federal" }
  ]

  const getColorClasses = (color: string) => {
    const colors = {
      blue: "from-blue-600 to-blue-700 text-blue-600 bg-blue-50 border-blue-200",
      green: "from-green-600 to-green-700 text-green-600 bg-green-50 border-green-200",
      purple: "from-purple-600 to-purple-700 text-purple-600 bg-purple-50 border-purple-200"
    }
    return colors[color as keyof typeof colors] || colors.blue
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-blue-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Volver al inicio</span>
              </Link>
            </div>
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">
                Instituciones Participantes
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Introducción */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Users className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Colaboración Interinstitucional</h2>
          </div>
          <div className="prose max-w-none text-gray-700">
            <p className="text-lg mb-4">
              El Sistema de Jerarquización de Cuencas de Chiapas es resultado de una colaboración 
              estratégica entre instituciones gubernamentales, académicas y de investigación, 
              uniendo esfuerzos para desarrollar herramientas científicas para la gestión sustentable 
              de los recursos hídricos.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <p className="font-semibold text-blue-800">Enfoque Colaborativo:</p>
              <p className="text-blue-700">
                Cada institución aporta su expertise específica, creando un enfoque multidisciplinario 
                que garantiza la robustez científica y la aplicabilidad práctica del sistema.
              </p>
            </div>
          </div>
        </div>

        {/* Instituciones Principales */}
        <div className="space-y-8 mb-12">
          {instituciones.map((institucion, index) => {
            const colorClasses = getColorClasses(institucion.color)
            return (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className={`bg-gradient-to-r ${colorClasses.split(' ')[0]} ${colorClasses.split(' ')[1]} p-6`}>
                  <div className="flex items-center space-x-4">
                    <div className="text-4xl">{institucion.logo}</div>
                    <div className="text-white">
                      <h3 className="text-2xl font-bold">{institucion.nombre}</h3>
                      <p className="text-blue-100">{institucion.dependencia}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  <p className="text-gray-700 mb-6 text-lg">{institucion.descripcion}</p>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Funciones */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <Target className="h-5 w-5 mr-2 text-blue-600" />
                        Funciones en el Proyecto
                      </h4>
                      <ul className="space-y-2">
                        {institucion.funciones.map((funcion, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <Award className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                            <span className="text-gray-700">{funcion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Contacto */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <Phone className="h-5 w-5 mr-2 text-blue-600" />
                        Información de Contacto
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <MapPin className="h-4 w-4 text-gray-500 mt-1" />
                          <span className="text-gray-700">{institucion.contacto.direccion}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-700">{institucion.contacto.telefono}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-700">{institucion.contacto.email}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Globe className="h-4 w-4 text-gray-500" />
                          <a 
                            href={`https://${institucion.contacto.web}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                          >
                            <span>{institucion.contacto.web}</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Instituciones Colaboradoras */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center space-x-3 mb-6">
            <Globe className="h-6 w-6 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-900">Instituciones Colaboradoras</h2>
          </div>
          
          <p className="text-gray-700 mb-6">
            Organismos federales que proporcionan datos, información técnica y apoyo metodológico:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {colaboradores.map((colaborador, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{colaborador.nombre}</h4>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {colaborador.tipo}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{colaborador.descripcion}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Reconocimientos */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-xl shadow-lg p-8 mt-8 text-white">
          <div className="text-center">
            <Award className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4">Reconocimiento</h3>
            <p className="text-blue-100 max-w-3xl mx-auto">
              Agradecemos la colaboración y compromiso de todas las instituciones participantes, 
              cuyo trabajo conjunto hace posible el desarrollo de herramientas científicas para 
              la conservación y manejo sustentable de los recursos hídricos de Chiapas.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Instituciones
